import {
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState,
} from 'react';

import { RenderIf } from '@/components/conditional/render-if';
import { ModalFooterCancelSave } from '@/components/modal/footers/modal-footer-cancel-save';

import { ModalContext } from './context';
import { ModalProviderRenderer } from './renderer';
import { ModalConfig } from './types';

export function ModalProvider({ children }: PropsWithChildren) {
  const [modalState, setModalState] = useState<any>({
    isOpen: false,
    title: '',
    template: null as ReactNode | null,
    footer: null as ReactNode | null,
    width: null,
  });

  function handleCloseModal() {
    setModalState({
      isOpen: false,
      title: '',
      template: null,
      footer: null,
      width: null,
      onClose: handleCloseModal,
      onSave: handleCloseModal,
    });
  }

  function handleOpenModal(config: ModalConfig) {
    setModalState({
      isOpen: true,
      title: config.title,
      template: config.template,
      width: config.width,
      onClose: config?.onClose ?? handleCloseModal,
      onSave: config?.onSave ?? handleCloseModal,
      footer: (
        <RenderIf
          condition={typeof config.footer === 'boolean' && config.footer === true}
          fallback={(
            <RenderIf
              condition={config.footer != null}
            >
              {config.footer}
            </RenderIf>
          )}
        >
          <ModalFooterCancelSave
            onSave={handleCloseModal}
            onCancel={handleCloseModal}
          />
        </RenderIf>
      ),
    });
  }

  const contextValue = useMemo(
    () => ({
      isOpen: modalState.isOpen,
      template: modalState.template,
      width: modalState.width,
      open: handleOpenModal,
      close: handleCloseModal,
    }),
    [
      modalState.isOpen,
      modalState.template,
      modalState.width,
      handleOpenModal,
      handleCloseModal,
    ],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <ModalProviderRenderer
        isOpen={modalState.isOpen}
        title={modalState.title}
        template={modalState.template}
        footer={modalState.footer}
        width={modalState.width}
        onClose={handleCloseModal}
      />
    </ModalContext.Provider>
  );
}

export default ModalProvider;
